import React from "react";
import { Minus, Plus, X, ShoppingBag } from "lucide-react";

const OrderPanel = ({
  cart,
  increaseQty,
  decreaseQty,
  removeItem,
  updateItemQty,
  placeOrder,
  loading,
  mode,
  onSwitchToTakeaway,
  takeawayEnabled,
  platformFee
}) => {

  const calculateSubtotal = () => {
    return cart.reduce(
      (total, item) => total + item.price * item.qty,
      0
    );
  };

  const platformFeeAmount = () => {
    if (!platformFee?.enabled) return 0;
    const subtotal = calculateSubtotal();
    return subtotal * (platformFee.percentage / 100);
  };

  const calculateGrandTotal = () => {
    return calculateSubtotal() + platformFeeAmount();
  };

  if (mode === "view") {
    return (
      <div className="flex flex-col items-center justify-center p-6 text-center h-full bg-slate-50 rounded-2xl border border-slate-100">
        <div className="w-12 h-12 bg-amber-50 rounded-2xl flex items-center justify-center text-amber-550 mb-4">
          <ShoppingBag className="w-6 h-6" />
        </div>
        <p className="text-base font-black text-gray-900 mb-1">View Only Mode</p>
        <p className="text-xs text-gray-400 font-semibold mb-6 max-w-[200px]">
          Dine-In tables are currently occupied or unavailable.
          {takeawayEnabled !== false ? " You can explore the menu or switch to takeaway." : " You can explore the menu."}
        </p>
        {takeawayEnabled !== false && (
          <button
            onClick={onSwitchToTakeaway}
            disabled={loading}
            className="w-full py-3 bg-teal-600 hover:bg-teal-700 text-white font-extrabold text-xs rounded-xl shadow-md transition uppercase tracking-wider flex items-center justify-center gap-1.5"
          >
            <ShoppingBag className="w-4 h-4" />
            Order Takeaway instead
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <p className="text-lg text-center text-teal-600 font-bold font-serif mb-4">
        Order Summary
      </p>

      {/* Items Table */}
      <div className="overflow-x-auto overflow-auto flex-1">

        {cart.length === 0 ? (
          <p className="text-gray-500 text-center mt-10">
            No items added
          </p>
        ) : (
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead>
              <tr>
                <th className="p-2 bg-gray-50 text-left">Item</th>
                <th className="p-2 bg-gray-50 text-center">Qty</th>
                <th className="p-2 bg-gray-50 text-center">Price</th>
                <th className="p-2 bg-gray-50 text-center">Remove</th>
              </tr>
            </thead>

            <tbody className="bg-white divide-y divide-gray-200">
              {cart.map((item) => (
                <tr key={item.cartKey || item.id}>

                  {/* Item Name */}
                  <td className="p-2 max-w-[120px]">
                    <div
                      className="truncate cursor-pointer font-semibold"
                      title={item.productName || item.name}
                    >
                      {item.productName || item.name}
                    </div>
                    {item.selectedVariant && (
                      <div className="text-[10px] text-indigo-600 font-bold">
                        Size: {item.selectedVariant.name}
                      </div>
                    )}
                    {item.selectedAddOns && item.selectedAddOns.length > 0 && (
                      <div className="text-[10px] text-teal-600 font-semibold mt-0.5 max-w-[120px] truncate" title={item.selectedAddOns.map(a => a.name).join(", ")}>
                        + {item.selectedAddOns.map(a => a.name).join(", ")}
                      </div>
                    )}
                  </td>

                  {/* Quantity Controls */}
                  <td className="p-2 text-center">
                    <div className="flex items-center justify-center gap-2">

                      <button
                        onClick={() => decreaseQty(item)}
                        disabled={item.qty <= 1}
                        className="bg-red-500 hover:bg-red-600 disabled:opacity-40
                                   text-white w-7 h-7 rounded-full
                                   flex items-center justify-center transition"
                      >
                        <Minus size={14} />
                      </button>

                      <input
                        type="number"
                        min="1"
                        value={item.qty}
                        onChange={(e) => {
                          const value = Math.max(
                            1,
                            Number(e.target.value) || 1
                          );
                          updateItemQty(item.cartKey || item.id, value);
                        }}
                        className="w-8 h-8 text-center rounded outline-none focus:ring-2 focus:ring-teal-500 appearance-none border"
                      />

                      <button
                        onClick={() => increaseQty(item)}
                        className="bg-green-500 hover:bg-green-600
                                   text-white w-7 h-7 rounded-full
                                   flex items-center justify-center transition"
                      >
                        <Plus size={14} />
                      </button>

                    </div>
                  </td>

                  {/* Price */}
                  <td className="py-2 px-3 text-nowrap text-center font-medium">
                    ₹ {(item.price * item.qty).toFixed(2)}
                  </td>

                  {/* Remove */}
                  <td className="py-2 px-3 text-center">
                    <button
                      onClick={() => removeItem(item)}
                      className="bg-red-400 text-white hover:bg-red-500
                                 w-8 h-8 rounded-full
                                 flex items-center justify-center transition"
                    >
                      <X size={14} />
                    </button>
                  </td>

                </tr>
              ))}
            </tbody>
          </table>
        )}

      </div>

      {/* Total Section */}
      {cart.length > 0 && (
        <div className="mt-4 border-t pt-4">

          <div className="flex justify-between text-sm text-gray-500 mb-2 font-semibold">
            <span>Subtotal</span>
            <span>₹ {calculateSubtotal().toFixed(2)}</span>
          </div>

          {platformFee?.enabled && (
            <div className="flex justify-between text-sm text-gray-500 mb-2 font-semibold">
              <span>Platform Fee ({platformFee.percentage}%)</span>
              <span>₹ {platformFeeAmount().toFixed(2)}</span>
            </div>
          )}

          <div className="flex justify-between text-lg font-bold mb-4 mt-2 pt-2 border-t border-gray-100">
            <span>Total</span>
            <span>₹ {calculateGrandTotal().toFixed(2)}</span>
          </div>

          <button
            onClick={placeOrder}
            disabled={loading}
            className={`w-full bg-teal-600 hover:bg-teal-700
                        text-white font-bold font-serif py-2 rounded-full
                        flex items-center justify-center gap-2
                        ${loading ? "opacity-70 cursor-not-allowed" : ""}`}
          >
            {loading ? (
              <>
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                Processing...
              </>
            ) : (
              "Confirm Order"
            )}
          </button>

        </div>
      )}

    </div>
  );
};

export default OrderPanel;
